<script setup lang="ts">
import { LogOut, Monitor, Moon, Siren, Sun, User } from "@lucide/vue";
import { ref } from "vue";
import { signOut } from "@/api/client";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSession } from "@/composables/useSession";
import { useTheme, type ThemePreference } from "@/composables/useTheme";
import { t } from "@/copy/strings";
import { initials } from "@/lib/format";
import BreakGlassDialog from "./BreakGlassDialog.vue";

const { me, displayName } = useSession();
const { preference, setTheme } = useTheme();
const breakGlassOpen = ref(false);

function markSignedOut() {
  try {
    sessionStorage.setItem("smake.signedOut", "1");
  } catch {
    /* ignore */
  }
  void signOut();
}
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <Button variant="ghost" class="h-9 gap-2 px-1.5 sm:px-2" :aria-label="`Account: ${displayName}`">
        <Avatar class="size-7">
          <AvatarFallback class="bg-primary/10 text-xs font-semibold text-primary">{{ initials(displayName) }}</AvatarFallback>
        </Avatar>
        <span class="hidden max-w-32 truncate text-sm font-medium sm:inline">{{ displayName }}</span>
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" class="w-60">
      <DropdownMenuLabel class="font-normal">
        <p class="truncate text-sm font-medium">{{ displayName }}</p>
        <p v-if="me?.email" class="truncate text-xs text-muted-foreground">{{ me.email }}</p>
        <p v-if="me?.roles.length" class="mt-1 truncate text-xs text-muted-foreground">{{ me.roles.join(", ") }}</p>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem as-child>
        <RouterLink to="/activity"><User /> {{ t.nav.activity }}</RouterLink>
      </DropdownMenuItem>
      <DropdownMenuItem v-if="me?.canBreakGlass" @select="breakGlassOpen = true">
        <Siren /> {{ t.admin.breakGlass }}…
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuLabel class="text-xs font-normal text-muted-foreground">{{ t.common.theme }}</DropdownMenuLabel>
      <DropdownMenuRadioGroup :model-value="preference" @update:model-value="(v) => setTheme(v as ThemePreference)">
        <DropdownMenuRadioItem value="light"><Sun class="size-4" /> {{ t.common.themeLight }}</DropdownMenuRadioItem>
        <DropdownMenuRadioItem value="dark"><Moon class="size-4" /> {{ t.common.themeDark }}</DropdownMenuRadioItem>
        <DropdownMenuRadioItem value="system"><Monitor class="size-4" /> {{ t.common.themeSystem }}</DropdownMenuRadioItem>
      </DropdownMenuRadioGroup>
      <DropdownMenuSeparator />
      <DropdownMenuItem @select="markSignedOut"><LogOut /> {{ t.auth.signOut }}</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
  <BreakGlassDialog v-model:open="breakGlassOpen" />
</template>
